import React, { useState, useEffect } from 'react';
import { db, auth, analytics, logEvent } from '../firebase';
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  deleteDoc,
  getDoc,
  onSnapshot,
  orderBy,
  limit,
  Timestamp
} from 'firebase/firestore';

function Friends() {
  const [activeTab, setActiveTab] = useState('list');
  const [friends, setFriends] = useState([]);
  const [friendRequests, setFriendRequests] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [myProfile, setMyProfile] = useState(null);

  // 채팅 관련
  const [selectedFriend, setSelectedFriend] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [sendingChat, setSendingChat] = useState(false);

  useEffect(() => {
    loadMyProfile();
    loadFriends();
    loadFriendRequests();
  }, []);

  // 채팅 실시간 리스너
  useEffect(() => {
    if (!selectedFriend) {
      setChatMessages([]);
      return;
    }

    const chatId = getChatId(auth.currentUser.uid, selectedFriend.friendId);
    const messagesRef = collection(db, 'chats', chatId, 'messages');
    const q = query(messagesRef, orderBy('createdAt', 'desc'), limit(50));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const messages = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // 24시간 이상 된 메시지 필터링
      const oneDayAgo = new Date();
      oneDayAgo.setHours(oneDayAgo.getHours() - 24);

      const recentMessages = messages.filter(msg => {
        if (!msg.createdAt) return true;
        const msgDate = msg.createdAt.toDate();
        return msgDate >= oneDayAgo;
      });

      setChatMessages(recentMessages.reverse());

      // 24시간 지난 메시지 삭제
      messages.forEach(async (msg) => {
        if (!msg.createdAt) return;
        const msgDate = msg.createdAt.toDate();
        if (msgDate < oneDayAgo) {
          await deleteDoc(doc(db, 'chats', chatId, 'messages', msg.id));
        }
      });
    });

    return () => unsubscribe();
  }, [selectedFriend]);

  const getChatId = (userId1, userId2) => {
    return [userId1, userId2].sort().join('_');
  };

  const loadMyProfile = async () => {
    const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
    if (userDoc.exists()) {
      setMyProfile(userDoc.data());
    }
  };

  const loadFriends = async () => {
    try {
      const friendsQuery = query(
        collection(db, 'friends'),
        where('userId', '==', auth.currentUser.uid)
      );
      const snapshot = await getDocs(friendsQuery);

      const friendsList = await Promise.all(
        snapshot.docs.map(async (d) => {
          const friendData = d.data();
          const friendDoc = await getDoc(doc(db, 'users', friendData.friendId));
          return {
            id: d.id,
            ...friendData,
            photoURL: friendDoc.exists() ? friendDoc.data().photoURL : null
          };
        })
      );

      setFriends(friendsList);
    } catch (error) {
      console.error('친구 목록 로드 실패:', error);
    }
  };

  const loadFriendRequests = async () => {
    try {
      const requestsQuery = query(
        collection(db, 'friend_requests'),
        where('toUserId', '==', auth.currentUser.uid),
        where('status', '==', 'pending')
      );
      const snapshot = await getDocs(requestsQuery);
      const requests = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setFriendRequests(requests);
    } catch (error) {
      console.error('친구 요청 로드 실패:', error);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setMessage('검색어를 입력하세요');
      return;
    }

    logEvent(analytics, 'search_friends', {
      query_length: searchQuery.length
    });

    setLoading(true);
    setMessage('');

    try {
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const results = [];

      usersSnapshot.docs.forEach(d => {
        const user = d.data();
        const userId = d.id;

        if (userId === auth.currentUser.uid) return;

        const isFriend = friends.some(f => f.friendId === userId);
        if (isFriend) return;

        if (
          user.nickname?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          user.email?.toLowerCase().includes(searchQuery.toLowerCase())
        ) {
          results.push({
            userId: userId,
            nickname: user.nickname,
            email: user.email,
            region: user.region
          });
        }
      });

      setSearchResults(results);
      if (results.length === 0) {
        setMessage('검색 결과가 없습니다');
      }
    } catch (error) {
      setMessage('검색 실패: ' + error.message);
    }
    setLoading(false);
  };

  const handleSendRequest = async (toUser) => {
    setLoading(true);
    setMessage('');

    try {
      const existingQuery = query(
        collection(db, 'friend_requests'),
        where('fromUserId', '==', auth.currentUser.uid),
        where('toUserId', '==', toUser.userId),
        where('status', '==', 'pending')
      );
      const existing = await getDocs(existingQuery);

      if (!existing.empty) {
        setMessage('이미 친구 요청을 보냈습니다');
        setLoading(false);
        return;
      }

      await addDoc(collection(db, 'friend_requests'), {
        fromUserId: auth.currentUser.uid,
        fromUserName: myProfile?.nickname || auth.currentUser.email,
        toUserId: toUser.userId,
        toUserName: toUser.nickname,
        status: 'pending',
        createdAt: new Date()
      });

      logEvent(analytics, 'send_friend_request', {
        to_user_id: toUser.userId
      });

      setMessage(`${toUser.nickname}님에게 친구 요청을 보냈습니다!`);
    } catch (error) {
      setMessage('요청 실패: ' + error.message);
    }
    setLoading(false);
  };

  const handleAcceptRequest = async (request) => {
    setLoading(true);

    try {
      await addDoc(collection(db, 'friends'), {
        userId: auth.currentUser.uid,
        friendId: request.fromUserId,
        friendName: request.fromUserName,
        createdAt: new Date()
      });

      await addDoc(collection(db, 'friends'), {
        userId: request.fromUserId,
        friendId: auth.currentUser.uid,
        friendName: myProfile?.nickname || auth.currentUser.email,
        createdAt: new Date()
      });

      await updateDoc(doc(db, 'friend_requests', request.id), {
        status: 'accepted'
      });

      loadFriends();
      loadFriendRequests();
      setMessage(`${request.fromUserName}님과 친구가 되었습니다!`);

      logEvent(analytics, 'accept_friend_request', {
        from_user_id: request.fromUserId
      });
    } catch (error) {
      setMessage('수락 실패: ' + error.message);
    }
    setLoading(false);
  };

  const handleRejectRequest = async (request) => {
    setLoading(true);

    try {
      await updateDoc(doc(db, 'friend_requests', request.id), {
        status: 'rejected'
      });

      loadFriendRequests();
      setMessage('친구 요청을 거절했습니다');

      logEvent(analytics, 'reject_friend_request', {
        from_user_id: request.fromUserId
      });
    } catch (error) {
      setMessage('거절 실패: ' + error.message);
    }
    setLoading(false);
  };

  const handleRemoveFriend = async (friend) => {
    if (!window.confirm(`${friend.friendName}님을 친구 목록에서 삭제하시겠습니까?`)) {
      return;
    }

    setLoading(true);

    try {
      const myFriendQuery = query(
        collection(db, 'friends'),
        where('userId', '==', auth.currentUser.uid),
        where('friendId', '==', friend.friendId)
      );
      const myFriendSnapshot = await getDocs(myFriendQuery);
      myFriendSnapshot.docs.forEach(async (d) => {
        await deleteDoc(doc(db, 'friends', d.id));
      });

      const theirFriendQuery = query(
        collection(db, 'friends'),
        where('userId', '==', friend.friendId),
        where('friendId', '==', auth.currentUser.uid)
      );
      const theirFriendSnapshot = await getDocs(theirFriendQuery);
      theirFriendSnapshot.docs.forEach(async (d) => {
        await deleteDoc(doc(db, 'friends', d.id));
      });

      loadFriends();
      setMessage('친구를 삭제했습니다');

      logEvent(analytics, 'remove_friend', {
        friend_id: friend.friendId
      });
    } catch (error) {
      setMessage('삭제 실패: ' + error.message);
    }
    setLoading(false);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!chatInput.trim() || !selectedFriend) {
      return;
    }

    setSendingChat(true);

    try {
      const chatId = getChatId(auth.currentUser.uid, selectedFriend.friendId);
      const messagesRef = collection(db, 'chats', chatId, 'messages');

      await addDoc(messagesRef, {
        text: chatInput.trim(),
        senderId: auth.currentUser.uid,
        senderName: myProfile?.nickname || '익명',
        createdAt: Timestamp.now()
      });

      setChatInput('');

      logEvent(analytics, 'send_chat_message', {
        friend_id: selectedFriend.friendId
      });
    } catch (error) {
      console.error('메시지 전송 실패:', error);
    }

    setSendingChat(false);
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>친구</h2>

      {/* 탭 네비게이션 */}
      <div style={styles.tabs}>
        <button
          onClick={() => setActiveTab('list')}
          style={{
            ...styles.tab,
            ...(activeTab === 'list' ? styles.tabActive : {})
          }}
        >
          친구 목록 ({friends.length})
        </button>
        <button
          onClick={() => setActiveTab('search')}
          style={{
            ...styles.tab,
            ...(activeTab === 'search' ? styles.tabActive : {})
          }}
        >
          친구 찾기
        </button>
        <button
          onClick={() => setActiveTab('requests')}
          style={{
            ...styles.tab,
            ...(activeTab === 'requests' ? styles.tabActive : {})
          }}
        >
          요청 ({friendRequests.length})
        </button>
      </div>

      {/* 메시지 */}
      {message && (
        <div style={styles.message}>{message}</div>
      )}

      {/* 친구 목록 */}
      {activeTab === 'list' && (
        <div style={styles.content}>
          {friends.length === 0 ? (
            <div style={styles.emptyCard}>
              <p>아직 친구가 없어요!</p>
              <p>친구 찾기에서 친구를 추가해보세요</p>
            </div>
          ) : (
            <div style={styles.list}>
              {friends.map((friend) => (
                <div
                  key={friend.id}
                  style={styles.card}
                  onClick={() => setSelectedFriend(friend)}
                >
                  <div style={styles.cardLeft}>
                    {friend.photoURL ? (
                      <img src={friend.photoURL} alt={friend.friendName} style={styles.friendPhoto} />
                    ) : (
                      <div style={styles.friendPhotoPlaceholder}>
                        {friend.friendName.charAt(0)}
                      </div>
                    )}
                    <div style={styles.cardInfo}>
                      <div style={styles.cardName}>
                        {friend.friendName}
                      </div>
                      <div style={styles.cardDate}>
                        친구 된 날: {friend.createdAt?.toDate().toLocaleDateString('ko-KR')}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveFriend(friend);
                    }}
                    style={styles.deleteButton}
                    disabled={loading}
                  >
                    삭제
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 친구 찾기 */}
      {activeTab === 'search' && (
        <div style={styles.content}>
          <div style={styles.searchBox}>
            <input
              type="text"
              placeholder="닉네임 또는 이메일로 검색"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              style={styles.searchInput}
            />
            <button
              onClick={handleSearch}
              style={styles.searchButton}
              disabled={loading}
            >
              검색
            </button>
          </div>

          {searchResults.length > 0 && (
            <div style={styles.list}>
              {searchResults.map((user) => (
                <div key={user.userId} style={styles.card}>
                  <div style={styles.cardInfo}>
                    <div style={styles.cardName}>{user.nickname}</div>
                    <div style={styles.cardEmail}>{user.email}</div>
                    {user.region && (
                      <div style={styles.cardRegion}>{user.region}</div>
                    )}
                  </div>
                  <button
                    onClick={() => handleSendRequest(user)}
                    style={styles.addButton}
                    disabled={loading}
                  >
                    친구 추가
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 친구 요청 */}
      {activeTab === 'requests' && (
        <div style={styles.content}>
          {friendRequests.length === 0 ? (
            <div style={styles.emptyCard}>
              <p>받은 친구 요청이 없어요!</p>
            </div>
          ) : (
            <div style={styles.list}>
              {friendRequests.map((request) => (
                <div key={request.id} style={styles.card}>
                  <div style={styles.cardInfo}>
                    <div style={styles.cardName}>
                      {request.fromUserName}
                    </div>
                    <div style={styles.cardDate}>
                      {request.createdAt?.toDate().toLocaleDateString('ko-KR')}
                    </div>
                  </div>
                  <div style={styles.requestButtons}>
                    <button
                      onClick={() => handleAcceptRequest(request)}
                      style={styles.acceptButton}
                      disabled={loading}
                    >
                      수락
                    </button>
                    <button
                      onClick={() => handleRejectRequest(request)}
                      style={styles.rejectButton}
                      disabled={loading}
                    >
                      거절
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 채팅 모달 */}
      {selectedFriend && (
        <div style={styles.modalOverlay} onClick={() => setSelectedFriend(null)}>
          <div style={styles.chatModal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.chatHeader}>
              <h3 style={styles.chatTitle}>{selectedFriend.friendName}</h3>
              <button onClick={() => setSelectedFriend(null)} style={styles.closeButton}>
                X
              </button>
            </div>

            <div style={styles.chatNotice}>
              메시지는 24시간 후 자동으로 삭제됩니다
            </div>

            <div style={styles.chatMessages}>
              {chatMessages.length === 0 ? (
                <div style={styles.chatEmpty}>
                  첫 메시지를 보내보세요!
                </div>
              ) : (
                chatMessages.map((msg) => (
                  <div
                    key={msg.id}
                    style={{
                      ...styles.chatMessage,
                      ...(msg.senderId === auth.currentUser.uid
                        ? styles.chatMessageMine
                        : styles.chatMessageTheirs)
                    }}
                  >
                    <div style={styles.chatMessageText}>{msg.text}</div>
                    <div style={styles.chatMessageTime}>
                      {msg.createdAt?.toDate().toLocaleTimeString('ko-KR', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                  </div>
                ))
              )}
            </div>

            <form onSubmit={handleSendMessage} style={styles.chatInputForm}>
              <input
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                placeholder="메시지를 입력하세요..."
                style={styles.chatInput}
                maxLength={200}
                disabled={sendingChat}
              />
              <button
                type="submit"
                style={styles.chatSendButton}
                disabled={sendingChat || !chatInput.trim()}
              >
                전송
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '600px',
    margin: '0 auto',
    padding: '16px'
  },
  title: {
    fontSize: '20px',
    fontWeight: '600',
    marginBottom: '12px',
    borderBottom: '1px solid #ddd',
    paddingBottom: '8px'
  },
  tabs: {
    display: 'flex',
    gap: '6px',
    marginBottom: '16px',
    borderBottom: '1px solid #ddd',
    paddingBottom: '8px'
  },
  tab: {
    flex: 1,
    padding: '8px 0',
    backgroundColor: 'transparent',
    color: '#666',
    border: 'none',
    borderBottom: '2px solid transparent',
    cursor: 'pointer',
    fontSize: '13px'
  },
  tabActive: {
    borderBottomColor: '#000',
    fontWeight: '600',
    color: '#000'
  },
  message: {
    padding: '10px',
    marginBottom: '12px',
    backgroundColor: '#eee',
    border: '1px solid #ddd',
    textAlign: 'center',
    fontSize: '13px'
  },
  content: {
    minHeight: '300px'
  },
  searchBox: {
    display: 'flex',
    gap: '8px',
    marginBottom: '16px'
  },
  searchInput: {
    flex: 1,
    padding: '8px',
    fontSize: '14px',
    border: '1px solid #ddd',
    outline: 'none'
  },
  searchButton: {
    padding: '8px 16px',
    backgroundColor: '#808080',
    color: 'white',
    border: '1px solid #000',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600'
  },
  emptyCard: {
    backgroundColor: '#fafafa',
    padding: '24px',
    border: '1px solid #ddd',
    textAlign: 'center',
    color: '#666',
    fontSize: '13px'
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  card: {
    backgroundColor: 'white',
    padding: '12px',
    border: '1px solid #ddd',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer'
  },
  cardLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flex: 1
  },
  friendPhoto: {
    width: '40px',
    height: '40px',
    objectFit: 'cover',
    border: '1px solid #ddd'
  },
  friendPhotoPlaceholder: {
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#eee',
    border: '1px solid #ddd',
    fontSize: '18px',
    fontWeight: '600',
    color: '#666'
  },
  cardInfo: {
    flex: 1
  },
  cardName: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '3px'
  },
  cardEmail: {
    fontSize: '12px',
    color: '#666',
    marginBottom: '2px'
  },
  cardRegion: {
    fontSize: '11px',
    color: '#666'
  },
  cardDate: {
    fontSize: '11px',
    color: '#999'
  },
  addButton: {
    padding: '6px 12px',
    backgroundColor: '#808080',
    color: 'white',
    border: '1px solid #000',
    cursor: 'pointer',
    fontSize: '12px'
  },
  deleteButton: {
    padding: '6px 12px',
    backgroundColor: '#fff',
    color: '#c62828',
    border: '1px solid #c62828',
    cursor: 'pointer',
    fontSize: '12px'
  },
  requestButtons: {
    display: 'flex',
    gap: '8px'
  },
  acceptButton: {
    padding: '6px 12px',
    backgroundColor: '#808080',
    color: 'white',
    border: '1px solid #000',
    cursor: 'pointer',
    fontSize: '12px'
  },
  rejectButton: {
    padding: '6px 12px',
    backgroundColor: '#fff',
    color: '#666',
    border: '1px solid #ccc',
    cursor: 'pointer',
    fontSize: '12px'
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  chatModal: {
    backgroundColor: '#fff',
    border: '1px solid #ccc',
    width: '90%',
    maxWidth: '500px',
    maxHeight: '80vh',
    display: 'flex',
    flexDirection: 'column'
  },
  chatHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px',
    borderBottom: '1px solid #ddd'
  },
  chatTitle: {
    margin: 0,
    fontSize: '16px',
    fontWeight: '600'
  },
  closeButton: {
    width: '28px',
    height: '28px',
    backgroundColor: '#fff',
    border: '1px solid #ddd',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600'
  },
  chatNotice: {
    padding: '8px 12px',
    backgroundColor: '#fafafa',
    border: '1px solid #eee',
    fontSize: '11px',
    color: '#666',
    textAlign: 'center'
  },
  chatMessages: {
    flex: 1,
    padding: '12px',
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    backgroundColor: '#fafafa'
  },
  chatEmpty: {
    textAlign: 'center',
    color: '#999',
    fontSize: '13px',
    padding: '24px'
  },
  chatMessage: {
    maxWidth: '70%',
    padding: '8px 12px',
    fontSize: '13px'
  },
  chatMessageMine: {
    alignSelf: 'flex-end',
    backgroundColor: '#000',
    color: '#fff',
    border: '1px solid #000'
  },
  chatMessageTheirs: {
    alignSelf: 'flex-start',
    backgroundColor: '#fff',
    color: '#333',
    border: '1px solid #ddd'
  },
  chatMessageText: {
    marginBottom: '4px',
    wordBreak: 'break-word'
  },
  chatMessageTime: {
    fontSize: '10px',
    opacity: 0.7
  },
  chatInputForm: {
    display: 'flex',
    gap: '8px',
    padding: '12px',
    borderTop: '1px solid #ddd'
  },
  chatInput: {
    flex: 1,
    padding: '8px',
    fontSize: '13px',
    border: '1px solid #ddd',
    outline: 'none'
  },
  chatSendButton: {
    padding: '8px 16px',
    backgroundColor: '#808080',
    color: 'white',
    border: '1px solid #000',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600'
  }
};

export default Friends;
